import { resolveTelemetryConfig } from "./config.js";
import { NoopTelemetryProvider } from "./noop.js";
import type { TelemetryConfig, TelemetryProvider } from "./types.js";

let _provider: TelemetryProvider = new NoopTelemetryProvider();
let _config: TelemetryConfig | undefined;

/**
 * Initialize the global telemetry provider. Called once at the entry point
 * (CLI command, startAgentServer, startAgentRepl). Lazy-loads the OTEL
 * provider when configured.
 *
 * This is the ONLY module-level state in the telemetry package. Packages
 * like bridge and connectors receive the provider via constructor injection
 * from the runner — they never call this function.
 *
 * @param rawConfig - The unvalidated `telemetry:` section of `skaile.yaml`, or
 *   `undefined` to use defaults (noop, no I/O).
 * @returns The initialized {@link TelemetryProvider} (noop or OTEL).
 * @docLink packages/telemetry/concepts#init-telemetry
 */
export async function initTelemetry(
  rawConfig?: Record<string, unknown>,
): Promise<TelemetryProvider> {
  const config = resolveTelemetryConfig(rawConfig ?? {});
  _config = config;

  if (config.provider === "none") {
    _provider = new NoopTelemetryProvider();
    return _provider;
  }

  if (config.provider === "otel") {
    const { OtelTelemetryProvider } = await import("./otel/provider.js");
    _provider = await OtelTelemetryProvider.create(config);
    return _provider;
  }

  _provider = new NoopTelemetryProvider();
  return _provider;
}

/**
 * Get the current telemetry provider. Returns the {@link NoopTelemetryProvider}
 * if {@link initTelemetry} has not yet been called.
 *
 * Bridge, runner, and connector packages use this only during construction —
 * they store the result and never call `getTelemetry()` on every span.
 *
 * @returns The active {@link TelemetryProvider}.
 * @docLink packages/telemetry/concepts#init-telemetry
 */
export function getTelemetry(): TelemetryProvider {
  return _provider;
}

/**
 * Get the resolved {@link TelemetryConfig}. Returns `undefined` before
 * {@link initTelemetry} is called.
 *
 * @returns The resolved config, or `undefined` if not yet initialized.
 * @docLink packages/telemetry/concepts#init-telemetry
 */
export function getTelemetryConfig(): TelemetryConfig | undefined {
  return _config;
}
